import { useQuery } from "@tanstack/react-query";
import { parse } from "date-fns";

import { QueryKeys } from "constants/query-keys";
import { ApiManager } from "services/api";

import { queryClient } from "../../queryClient";

export type IInvitationMode = "strict" | "lax";

export interface IOrganization {
  organizationId: string;
  name: string;
  company: ICompany;
  products: IProduct[];
  invitationMode: IInvitationMode;
  createdAt: Date;
  updatedAt: Date;
}

export interface ICompany {
  website: string;
  companySize: string;
  industry: string;
}

export interface IProduct {
  product: string;
  clientId?: string;
  createdAt: Date;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function organizationSerializer(data: any): IOrganization {
  return {
    organizationId: data?.organization_id,
    name: data?.name,
    company: {
      website: data?.company?.website,
      companySize: data?.company?.company_size,
      industry: data?.company?.industry,
    },
    products: data?.products.map((product: { product: string; client_id: string; created_at: string }) => ({
      product: product?.product,
      clientId: product?.client_id,
      createdAt: parse(product?.created_at),
    })),
    invitationMode: data?.invitation_mode,
    createdAt: parse(data?.created_at),
    updatedAt: parse(data?.updated_at),
  };
}

const fetchOrganization = async (): Promise<unknown> => {
  const { result, error } = await ApiManager.globalAccountsApi.fetchOrganization();
  if (error) {
    throw error;
  }
  return result;
};

export const prefetchOrganization = async (): Promise<void> => {
  await queryClient.prefetchQuery([QueryKeys.Organization], fetchOrganization);
};

interface IUseOrganization {
  organization: IOrganization;
  isLoading: boolean;
  isSuccess: boolean;
}

export const useOrganization = (): IUseOrganization => {
  const { data, isLoading, isSuccess } = useQuery({
    queryKey: [QueryKeys.Organization],
    queryFn: fetchOrganization,
  });
  return { organization: organizationSerializer(data), isLoading, isSuccess };
};
